import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Switch,
  Typography,
  SelectChangeEvent,
  Alert,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Question, QuestionOption } from '../../types';
import { pdfs, images } from '../../services/api';

// Question types centralized here
// VIDEO UPLOAD: Added .mp4 and .mov support for VIDEO_PLAYER type.
export const QUESTION_TYPES = {
  RATING: 'rating',
  MULTIPLE_CHOICE: 'multiple_choice',
  TEXT: 'text',
  BOOLEAN: 'boolean',
  MULTIPLE_SELECT: 'multiple_select',
  MULTIPLE_REVEAL: 'multiple_reveal',
  AUDIO: 'audio',
  TEXT_OR_AUDIO: 'text_or_audio',
  DESCRIPTION: 'description',
  DRAG_AND_DROP: 'drag_and_drop',
  HORIZONTAL_DRAG_DROP: 'horizontal_drag_drop',
  STYLED_BOOLEAN: 'styled_boolean',
  VIDEO_PLAYER: 'video_player',
  PDF: 'pdf',
  CALLBACK_SUMMARY: 'callback_summary',
} as const;

interface Option {
  front_text: string;
  back_text: string;
  value: number;
  is_other?: boolean;
}

interface NewQuestion {
  text: string;
  category: string;
  question_type: string;
  low_text?: string;
  high_text?: string;
  options: Option[];
  video_url?: string;
  video_file?: File;
  pdf_url?: string;
  pdf_file?: File;
  image_file?: File;
  visual_component_url?: string;
  use_external_url?: boolean;
  is_active: boolean;
  use_ai_summary?: boolean;
  key?: string;
  metadata?: {
    use_ai_summary: boolean;
  };
}

interface CreateQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  error?: string | null;
  inFlow?: boolean;
  availableQuestions?: Question[];
  PaperProps?: {
    sx: {
      bgcolor: string;
      boxShadow: number;
      '& .MuiDialogTitle-root, & .MuiDialogContent-root'?: {
        color: string;
      };
      '& .MuiInputBase-root'?: {
        bgcolor: string;
        color: string;
      };
      '& .MuiInputLabel-root'?: {
        color: string;
      };
      '& .MuiSelect-icon'?: {
        color: string;
      };
      '& .MuiMenuItem-root'?: {
        bgcolor: string;
        color: string;
        '&:hover'?: {
          bgcolor: string;
        };
      };
      '& .MuiMenu-paper'?: {
        bgcolor: string;
        '& .MuiList-root'?: {
          bgcolor: string;
          padding?: number;
        };
      };
      '& .MuiButton-root'?: {
        color: string;
      };
      '& .MuiPopover-paper'?: {
        bgcolor: string;
      };
    };
  };
}

const CreateQuestionDialog: React.FC<CreateQuestionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
  inFlow = false,
  availableQuestions = [],
  PaperProps,
}) => {
  const [newQuestion, setNewQuestion] = useState<NewQuestion>({
    text: '',
    category: '',
    question_type: QUESTION_TYPES.MULTIPLE_CHOICE,
    options: [],
    video_url: '',
    video_file: undefined,
    pdf_url: '',
    pdf_file: undefined,
    use_external_url: false,
    is_active: true,
    key: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [useAiSummary, setUseAiSummary] = useState(true);
  const [text, setText] = useState('');

  // --- VIDEO UPLOAD HANDLER ---
  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Only accept .mp4 or .mov
      const validTypes = ['video/mp4', 'video/quicktime'];
      if (!validTypes.includes(file.type)) {
        setLocalError('Only .mp4 and .mov video files are supported');
        return;
      }
      setNewQuestion(prev => ({ ...prev, video_file: file }));
      setLocalError(null);
    }
  };
  // --- END VIDEO UPLOAD HANDLER ---

  // --- IMAGE UPLOAD HANDLER ---
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Only accept image files
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setLocalError('Only JPG, PNG, GIF and WebP image files are supported');
        return;
      }
      
      try {
        setIsLoading(true);
        setLocalError(null);
        
        // Upload the image directly to Digital Ocean S3
        const imageKey = await images.upload(file);
        console.log('Image uploaded successfully, key:', imageKey);
        
        // Store the image key in the visual_component_url field
        setNewQuestion(prev => ({ 
          ...prev, 
          visual_component_url: imageKey,
          image_file: file // Keep the file reference for UI display purposes
        }));
      } catch (error) {
        console.error('Error uploading image:', error);
        setLocalError('Failed to upload image. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };
  // --- END IMAGE UPLOAD HANDLER ---

  const handleClose = () => {
    setSubmitted(false);
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateQuestion()) {
      setLocalError("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    setLocalError(null);

    try {
      const metadata = newQuestion.question_type === QUESTION_TYPES.CALLBACK_SUMMARY ? {
        use_ai_summary: useAiSummary
      } : undefined;

      // Clean up fields: remove metadata, key, pdf_file, correct_boolean_value if undefined or not needed
      const questionData: any = {
        ...newQuestion,
        text: text,
      };
      // Only include key if it's a non-empty string
      if (newQuestion.key && newQuestion.key !== 'undefined') {
        questionData.key = newQuestion.key;
      } else if (newQuestion.question_type === QUESTION_TYPES.CALLBACK_SUMMARY) {
        questionData.key = 'callback_summary';
      }
      // Only include metadata if it's valid
      if (metadata) {
        questionData.metadata = JSON.stringify(metadata);
      }
      // Only include correct_boolean_value for BOOLEAN question type
      if (questionData.question_type !== QUESTION_TYPES.BOOLEAN && 'correct_boolean_value' in questionData) {
        delete questionData.correct_boolean_value;
      }
      // Remove pdf_file if it's undefined
      if (questionData.pdf_file === undefined || questionData.pdf_file === 'undefined') {
        delete questionData.pdf_file;
      }
      // Remove image_file since we've already uploaded it and have the URL in visual_component_url
      if ('image_file' in questionData) {
        delete questionData.image_file;
      }
      // Remove metadata if it's undefined or the string 'undefined'
      if (questionData.metadata === undefined || questionData.metadata === 'undefined') {
        delete questionData.metadata;
      }
      // Remove key if it's undefined or the string 'undefined'
      if (questionData.key === undefined || questionData.key === 'undefined') {
        delete questionData.key;
      }

      await onSubmit(questionData);
      if (!inFlow) {
        handleClose();
      }
    } catch (err) {
      console.error('Error creating question:', err);
      setLocalError('Failed to create question');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setNewQuestion({
      text: '',
      category: '',
      question_type: QUESTION_TYPES.MULTIPLE_CHOICE,
      options: [],
      video_url: '',
      video_file: undefined,
      pdf_url: '',
      pdf_file: undefined,
      image_file: undefined,
      visual_component_url: '',
      use_external_url: false,
      is_active: true,
      key: ''
    });
    setLocalError(null);
    setUseAiSummary(true);
    setText('');
  };

  const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setIsLoading(true);
        setLocalError(null);
        const key = await pdfs.upload(file);
        setNewQuestion(prev => ({
          ...prev,
          pdf_url: key,
          pdf_file: file
        }));
      } catch (err) {
        console.error('Error uploading PDF:', err);
        setLocalError('Failed to upload PDF');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const validateOptions = (options: Option[]): boolean => {
    // Only require front text to be filled
    return options.every(option => 
      option.front_text.trim() !== '' && 
      option.value !== undefined
    );
  };

  const validateQuestion = (): boolean => {
    // Only category is required for all question types
    if (!newQuestion.category.trim()) {
      return false;
    }

    const optionRequiredTypes = [
      QUESTION_TYPES.MULTIPLE_CHOICE,
      QUESTION_TYPES.MULTIPLE_SELECT,
      QUESTION_TYPES.MULTIPLE_REVEAL,
      QUESTION_TYPES.DRAG_AND_DROP
    ] as const;

    if (optionRequiredTypes.includes(newQuestion.question_type as typeof optionRequiredTypes[number])) {
      return newQuestion.options.length > 0 && validateOptions(newQuestion.options);
    }

    // For PDF questions
    if (newQuestion.question_type === QUESTION_TYPES.PDF) {
      if (newQuestion.use_external_url) {
        return !!newQuestion.pdf_url;
      } else {
        return !!newQuestion.pdf_file;
      }
    }

    // For video questions
    if (newQuestion.question_type === QUESTION_TYPES.VIDEO_PLAYER) {
      if (newQuestion.use_external_url) {
        return !!newQuestion.video_url;
      } else {
        return !!newQuestion.video_file;
      }
    }

    // For callback summary questions
    if (newQuestion.question_type === QUESTION_TYPES.CALLBACK_SUMMARY) {
      return true; // Allow creation regardless of AI toggle state
    }

    return true;
  };

  const renderQuestionTypeFields = () => {
    switch (newQuestion.question_type) {
      case QUESTION_TYPES.RATING:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Low Text"
              value={newQuestion.low_text}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, low_text: e.target.value }))}
            />
            <TextField
              fullWidth
              label="High Text"
              value={newQuestion.high_text}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, high_text: e.target.value }))}
            />
          </Box>
        );
      case "image":
        return (
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
              style={{ display: 'none' }}
              id="image-file-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadFileIcon />}
                disabled={isLoading}
              >
                Upload Image
              </Button>
            </label>
            {newQuestion.image_file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {`Selected: ${newQuestion.image_file.name}`}
              </Typography>
            )}
          </Box>
        );
      case QUESTION_TYPES.PDF:
        return (
          <>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={newQuestion.use_external_url}
                    onChange={(e) => setNewQuestion(prev => ({
                      ...prev,
                      use_external_url: e.target.checked,
                      pdf_url: '',
                      pdf_file: undefined
                    }))}
                  />
                }
                label="Use External URL"
              />
            </FormControl>

            {newQuestion.use_external_url ? (
              <TextField
                fullWidth
                label="PDF URL"
                value={newQuestion.pdf_url}
                onChange={(e) => setNewQuestion(prev => ({
                  ...prev,
                  pdf_url: e.target.value
                }))}
              />
            ) : (
              <Box sx={{ mt: 2 }}>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="pdf-file-upload"
                  type="file"
                  onChange={handlePDFUpload}
                />
                <label htmlFor="pdf-file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    disabled={isLoading}
                  >
                    Upload PDF
                  </Button>
                </label>
                {newQuestion.pdf_url && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    PDF uploaded successfully
                  </Typography>
                )}
              </Box>
            )}
          </>
        );
      case QUESTION_TYPES.VIDEO_PLAYER:
        return (
          <>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={newQuestion.use_external_url}
                    onChange={(e) => setNewQuestion(prev => ({
                      ...prev,
                      use_external_url: e.target.checked,
                      video_url: '',
                      video_file: undefined
                    }))}
                  />
                }
                label="Use External URL"
              />
            </FormControl>
            {newQuestion.use_external_url ? (
              <TextField
                fullWidth
                label="Video URL"
                value={newQuestion.video_url}
                onChange={(e) => setNewQuestion(prev => ({
                  ...prev,
                  video_url: e.target.value
                }))}
              />
            ) : (
              <Box sx={{ mt: 2 }}>
                <input
                  accept="video/mp4,video/quicktime,.mp4,.mov"
                  style={{ display: 'none' }}
                  id="video-file-upload"
                  type="file"
                  onChange={handleVideoUpload}
                />
                <label htmlFor="video-file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    disabled={isLoading}
                  >
                    Upload Video
                  </Button>
                </label>
                {newQuestion.video_file && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {`Selected: ${newQuestion.video_file.name}`}
                  </Typography>
                )}
              </Box>
            )}
          </>
        );
      case QUESTION_TYPES.CALLBACK_SUMMARY:
        return (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={useAiSummary}
                  onChange={(e) => setUseAiSummary(e.target.checked)}
                />
              }
              label="Use AI-Generated Summary"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const renderBasicFields = () => {
    return (
      <>
        <TextField
          fullWidth
          label="Question Text (Optional)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          rows={4}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Category"
          value={newQuestion.category}
          onChange={(e) => setNewQuestion(prev => ({ ...prev, category: e.target.value }))}
          error={submitted && newQuestion.category.trim() === ''}
          helperText={submitted && newQuestion.category.trim() === '' ? 'Category is required' : ''}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Question Key"
          value={newQuestion.key}
          onChange={(e) => setNewQuestion(prev => ({ ...prev, key: e.target.value }))}
          helperText="A unique identifier for this question (optional)"
          sx={{ mb: 2 }}
        />
        
        {/* Image Upload Section - Available for all question types */}
        <Box sx={{ mb: 2, border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Question Image (Optional)
          </Typography>
          <input
            accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
            style={{ display: 'none' }}
            id="image-file-upload"
            type="file"
            onChange={handleImageUpload}
          />
          <label htmlFor="image-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              disabled={isLoading}
              size="small"
            >
              Upload Image
            </Button>
          </label>
          {newQuestion.image_file && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {`Selected: ${newQuestion.image_file.name}`}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setNewQuestion(prev => ({ ...prev, image_file: undefined }))}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="question-type-label">Question Type</InputLabel>
          <Select
            labelId="question-type-label"
            value={newQuestion.question_type}
            label="Question Type"
            onChange={(e: SelectChangeEvent<string>) => {
              const type = e.target.value;
              setNewQuestion({
                ...newQuestion,
                question_type: type,
                // Set default key for callback summary
                key: type === QUESTION_TYPES.CALLBACK_SUMMARY ? 'callback_summary' : newQuestion.key
              });
            }}
          >
            {Object.entries(QUESTION_TYPES).map(([key, value]) => (
              <MenuItem key={value} value={value}>
                {key.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={PaperProps || {
        sx: {
          bgcolor: '#121212',  // Almost black
          boxShadow: 24,
          '& .MuiDialogTitle-root, & .MuiDialogContent-root': {
            color: '#fff'
          },
          '& .MuiInputBase-root': {
            bgcolor: '#1e1e1e',  // Very dark gray for inputs
            color: '#fff'
          },
          '& .MuiInputLabel-root': {
            color: '#fff'
          },
          '& .MuiSelect-icon': {
            color: '#fff'
          },
          '& .MuiMenuItem-root': {
            bgcolor: '#1e1e1e',
            color: '#fff',
            '&:hover': {
              bgcolor: '#2d2d2d'
            }
          },
          '& .MuiMenu-paper': {
            bgcolor: '#1e1e1e',
            '& .MuiList-root': {
              bgcolor: '#1e1e1e',
              padding: 0
            }
          },
          '& .MuiButton-root': {
            color: '#fff'
          },
          '& .MuiPopover-paper': {
            bgcolor: '#1e1e1e'
          }
        }
      }}
    >
      <DialogTitle>Create New Question</DialogTitle>
      <DialogContent>
        {(localError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(localError || error)}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {renderBasicFields()}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Options
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {newQuestion.options.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Front Text"
                    value={option.front_text}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[index] = { ...option, front_text: e.target.value };
                      setNewQuestion(prev => ({ ...prev, options: newOptions }));
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Back Text"
                    value={option.back_text}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[index] = { ...option, back_text: e.target.value };
                      setNewQuestion(prev => ({ ...prev, options: newOptions }));
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value"
                    type="number"
                    value={option.value}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[index] = { ...option, value: Number(e.target.value) };
                      setNewQuestion(prev => ({ ...prev, options: newOptions }));
                    }}
                    size="small"
                    sx={{ width: '100px' }}
                  />
                  {(newQuestion.question_type === QUESTION_TYPES.MULTIPLE_CHOICE || 
                    newQuestion.question_type === QUESTION_TYPES.MULTIPLE_SELECT) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={option.is_other || false}
                          onChange={(e) => {
                            const newOptions = [...newQuestion.options];
                            newOptions[index] = { ...option, is_other: e.target.checked };
                            setNewQuestion(prev => ({ ...prev, options: newOptions }));
                          }}
                          size="small"
                        />
                      }
                      label="Other"
                    />
                  )}
                  <IconButton 
                    onClick={() => {
                      const newOptions = newQuestion.options.filter((_, i) => i !== index);
                      setNewQuestion(prev => ({ ...prev, options: newOptions }));
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  const newOptions = [...newQuestion.options, { front_text: '', back_text: '', value: 0, is_other: false }];
                  setNewQuestion(prev => ({ ...prev, options: newOptions }));
                }}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Option
              </Button>
            </Box>
          </Box>

          {renderQuestionTypeFields()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isSubmitting || !validateQuestion()}
        >
          {(isLoading || isSubmitting) ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateQuestionDialog;
